import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert";
import {
  Plus,
  Trash2,
  Edit,
  Image,
  Video,
  FileJson,
  X,
  Crown,
  Upload,
  Play,
  Loader,
} from "lucide-react";
import { DOMAIN } from "../../strings/CONSTANTS";

// ── All API calls use /api/ prefix consistently ───────────────
const API = `${DOMAIN}/api`;

const SurpriseContent: React.FC = () => {
  const [contents, setContents] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState<any>(null);
  const [filterType, setFilterType] = useState<string>("");
  const [filterCategory, setFilterCategory] = useState<string>("");

  // Form state
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"GIF" | "Video" | "Image">("GIF");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFilePreview, setImageFilePreview] = useState("");
  const [contentFile, setContentFile] = useState<File | null>(null);
  const [contentFilePreview, setContentFilePreview] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoFilePreview, setVideoFilePreview] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Tracks whether the selected GIF-slot file is actually a video (for label)
  const [gifSlotIsVideo, setGifSlotIsVideo] = useState(false);

  // ── Fetch helpers ─────────────────────────────────────────────

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${DOMAIN}/surprise/category/list`);
      if (res.data.success) setCategories(res.data.data);
    } catch (err) {
      console.error("fetchCategories error:", err);
    }
  };

  const fetchContents = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterType) params.type = filterType;
      if (filterCategory) params.categoryId = filterCategory;
      const res = await axios.get(`${DOMAIN}/surprise/content/list`, { params });
      if (res.data.success) setContents(res.data.data);
    } catch (err) {
      console.error("fetchContents error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchContents();
  }, [filterType, filterCategory]);

  // ── File input handlers ───────────────────────────────────────

  /**
   * GIF slot: accepts GIF files OR video files (video→GIF on server).
   * If a file is already selected, ignore the new pick.
   */
  const handleGifFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Guard: don't replace an already-selected file
    if (contentFile) {
      e.target.value = "";
      return;
    }

    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-selected after clear
    if (!file) return;

    const isGif =
      file.type === "image/gif" || file.name.toLowerCase().endsWith(".gif");
    const isVideo =
      file.type.startsWith("video/") ||
      /\.(mp4|mov|avi|webm|mkv|mpeg)$/i.test(file.name);

    if (!isGif && !isVideo) {
      Swal(
        "Invalid Format",
        "Please upload a GIF or a video file (MP4, MOV, AVI, WEBM) to auto-convert to GIF",
        "error"
      );
      return;
    }

    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      Swal(
        "File Too Large",
        `Max size is ${isVideo ? "50MB for video" : "10MB for GIF"}. Your file is ${(
          file.size /
          (1024 * 1024)
        ).toFixed(1)}MB`,
        "error"
      );
      return;
    }

    setGifSlotIsVideo(isVideo);
    setContentFile(file);
    setContentFilePreview(URL.createObjectURL(file));
  };

  /**
   * Image slot: only image files. Guard against replacing an existing pick.
   */
  const handleImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Guard: don't replace an already-selected file
    if (imageFile) {
      e.target.value = "";
      return;
    }

    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal("Invalid Format", "Please upload an image file (PNG, JPG, WEBP)", "error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      Swal(
        "File Too Large",
        `Image is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Max 10MB`,
        "error"
      );
      return;
    }

    setImageFile(file);
    setImageFilePreview(URL.createObjectURL(file));
  };

  /**
   * Video slot: only video files (MP4, MOV, AVI, WEBM, MKV)
   * Max size: 50MB, duration: 5-60 seconds
   */
  const handleVideoFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Guard: don't replace an already-selected file
    if (videoFile) {
      e.target.value = "";
      return;
    }

    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    // Check if it's a video file
    if (!file.type.startsWith("video/") && !/\.(mp4|mov|avi|webm|mkv|mpeg)$/i.test(file.name)) {
      Swal("Invalid Format", "Please upload a video file (MP4, MOV, AVI, WEBM, MKV)", "error");
      return;
    }

    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      Swal(
        "File Too Large",
        `Video is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Max 50MB`,
        "error"
      );
      return;
    }

    // Check video duration (5-60 seconds)
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    videoElement.src = objectUrl;

    videoElement.onloadedmetadata = () => {
      const duration = videoElement.duration;
      URL.revokeObjectURL(objectUrl);

      if (duration < 5) {
        Swal(
          "Video Too Short",
          `Video duration is ${Math.round(duration)} seconds. Minimum is 5 seconds.`,
          "error"
        );
        return;
      }

      if (duration > 60) {
        Swal(
          "Video Too Long",
          `Video duration is ${Math.round(duration)} seconds. Maximum is 60 seconds for short content.`,
          "error"
        );
        return;
      }

      // Valid video - set the file
      setVideoFile(file);
      setVideoFilePreview(objectUrl);
    };

    videoElement.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      Swal(
        "Invalid Video",
        "Could not read video metadata. Please ensure it's a valid video file.",
        "error"
      );
    };
  };

  // ── Submit ────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!categoryId || !title.trim()) {
      Swal("Error", "Category and Title are required", "error");
      return;
    }

    if (type === "GIF" && !contentFile && !editingContent) {
      Swal("Error", "Please upload a GIF or a video file to convert", "error");
      return;
    }
    if (type === "Image" && !imageFile && !editingContent) {
      Swal("Error", "Please upload an image file", "error");
      return;
    }
    if (type === "Video" && !videoFile && !editingContent) {
      Swal("Error", "Please upload a video file", "error");
      return;
    }

    setIsUploading(true);
    setLoading(true);

    try {
      let finalContentUrl = "";
      let contentType = "file";

      // ── Upload GIF or convert video → GIF ─────────────────────
      if (type === "GIF" && contentFile) {
        const formData = new FormData();
        formData.append("file", contentFile);

        const endpoint = gifSlotIsVideo
          ? `${DOMAIN}/upload/video-to-gif`
          : `${DOMAIN}/upload/surprise-gif`;

        const uploadRes = await axios.post(endpoint, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!uploadRes.data.success) {
          throw new Error(uploadRes.data.message || "Failed to upload / convert GIF");
        }
        finalContentUrl = uploadRes.data.url;
        contentType = "file";
      }

      // ── Upload Image ──────────────────────────────────────────
      if (type === "Image" && imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);

        const uploadRes = await axios.post(`${DOMAIN}/upload/surprise-image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!uploadRes.data.success) {
          throw new Error(uploadRes.data.message || "Failed to upload image");
        }
        finalContentUrl = uploadRes.data.url;
        contentType = "file";
      }

      // ── Upload Video ──────────────────────────────────────────
      if (type === "Video" && videoFile) {
        const formData = new FormData();
        formData.append("file", videoFile);

        const uploadRes = await axios.post(`${DOMAIN}/upload/surprise-video`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!uploadRes.data.success) {
          throw new Error(uploadRes.data.message || "Failed to upload video");
        }
        finalContentUrl = uploadRes.data.url;
        contentType = "file";
      }

      const payload: any = {
        categoryId,
        title: title.trim(),
        type,
        contentType,
        contentUrl: finalContentUrl,
        isPremium,
      };

      let res;
      if (editingContent) {
        res = await axios.put(
          `${DOMAIN}/surprise/content/update/${editingContent._id}`,
          payload
        );
      } else {
        res = await axios.post(`${DOMAIN}/surprise/content/create`, payload);
      }

      if (res.data.success) {
        Swal("Success", editingContent ? "Content updated" : "Content created", "success");
        resetForm();
        fetchContents();
      }
    } catch (err: any) {
      Swal(
        "Error",
        err.response?.data?.message || err.message || "Something went wrong",
        "error"
      );
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────

  const handleDelete = async (id: string, contentTitle: string) => {
    const result = await Swal({
      title: "Delete Content?",
      text: `Are you sure you want to delete "${contentTitle}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!result) return;
    try {
      const res = await axios.delete(`${DOMAIN}/surprise/content/delete/${id}`);
      if (res.data.success) {
        Swal("Deleted!", "Content deleted successfully", "success");
        fetchContents();
      }
    } catch (err: any) {
      Swal("Error", err.response?.data?.message || "Cannot delete content", "error");
    }
  };

  // ── Form helpers ──────────────────────────────────────────────

  const resetForm = () => {
    setShowModal(false);
    setEditingContent(null);
    setCategoryId("");
    setTitle("");
    setType("GIF");
    setContentFile(null);
    setContentFilePreview("");
    setImageFile(null);
    setImageFilePreview("");
    setVideoFile(null);
    setVideoFilePreview("");
    setIsPremium(false);
    setGifSlotIsVideo(false);
  };

  const openEditModal = (content: any) => {
    setEditingContent(content);
    setCategoryId(content.categoryId?._id || content.categoryId);
    setTitle(content.title);
    setType(content.type);
    setContentFile(null);
    setContentFilePreview(
      content.type === "GIF" && content.contentType === "file"
        ? content.contentUrl
        : ""
    );
    setImageFile(null);
    setImageFilePreview(
      content.type === "Image" && content.contentType === "file"
        ? content.contentUrl
        : ""
    );
    setVideoFile(null);
    setVideoFilePreview(
      content.type === "Video" && content.contentType === "file"
        ? content.contentUrl
        : ""
    );
    setIsPremium(content.isPremium || false);
    setGifSlotIsVideo(false);
    setShowModal(true);
  };

  // ── Display helpers ───────────────────────────────────────────

  const getTypeIcon = (t: string) => {
    switch (t) {
      case "GIF":   return <FileJson className="w-4 h-4 text-purple-600" />;
      case "Video": return <Video className="w-4 h-4 text-blue-600" />;
      case "Image": return <Image className="w-4 h-4 text-green-600" />;
      default:      return null;
    }
  };

  /** Prepend DOMAIN if the stored URL is a relative path */
  const getFullUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${DOMAIN}${url}`;
  };

  const getLocalPlaceholder = (text: string) =>
    `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' font-size='14' fill='%239ca3af' text-anchor='middle' dy='.3em'%3E${encodeURIComponent(
      text
    )}%3C/text%3E%3C/svg%3E`;

  const renderContentPreview = (content: any) => {
    const fullUrl = getFullUrl(content.contentUrl);
    const t = content.type;

    if (t === "GIF") {
      return (
        <img
          src={fullUrl || getLocalPlaceholder("GIF not found")}
          className="w-full h-48 object-cover"
          alt={content.title}
          onError={(e) => {
            (e.target as HTMLImageElement).src = getLocalPlaceholder("GIF error");
          }}
        />
      );
    }
    if (t === "Video") {
      return (
        <div className="relative w-full h-48 bg-gray-900 flex items-center justify-center">
          {fullUrl ? (
            <video className="w-full h-full object-cover" src={fullUrl} controls />
          ) : (
            <span className="text-white text-sm">Video not found</span>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 pointer-events-none">
            <Play className="w-12 h-12 text-white opacity-80" />
          </div>
        </div>
      );
    }
    if (t === "Image") {
      return (
        <img
          src={fullUrl || getLocalPlaceholder("No image")}
          className="w-full h-48 object-cover"
          alt={content.title}
          onError={(e) => {
            (e.target as HTMLImageElement).src = getLocalPlaceholder("Image error");
          }}
        />
      );
    }
    return (
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
        {getTypeIcon(t)}
        <span className="ml-2 text-gray-500">{t}</span>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Surprise QR Content</h1>
            <p className="text-slate-600 mt-1">Manage GIFs, Videos, and Images for Surprise QR</p>
            <p className="text-sm text-purple-600 mt-1">
              💡 GIFs: Upload .gif or any video (auto-converts) | Images: Upload file | Videos: Upload MP4/MOV/WEBM (5-60s, max 50MB)
            </p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-5 h-5" /> Add Content
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>{cat.name}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Types</option>
              <option value="GIF">GIF</option>
              <option value="Video">Video</option>
              <option value="Image">Image</option>
            </select>
            <button
              onClick={() => { setFilterType(""); setFilterCategory(""); }}
              className="px-4 py-2 text-slate-600 hover:text-slate-800"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && (
            <div className="col-span-full text-center py-8 text-slate-500">Loading...</div>
          )}
          {!loading && contents.length === 0 && (
            <div className="col-span-full text-center py-8 text-slate-500">No content found</div>
          )}
          {contents.map((content) => (
            <div
              key={content._id}
              className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="relative">
                {renderContentPreview(content)}
                {content.isPremium && (
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white rounded-full p-1">
                    <Crown className="w-4 h-4" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {getTypeIcon(content.type)}
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                    {content.type}
                  </span>
                  <span className="text-xs text-slate-400">{content.categoryName}</span>
                </div>
                <h3 className="font-semibold text-slate-800 mb-2 line-clamp-2">{content.title}</h3>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEditModal(content)}
                    className="flex-1 px-3 py-1.5 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                  >
                    <Edit className="w-4 h-4 inline mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(content._id, content.title)}
                    className="flex-1 px-3 py-1.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm"
                  >
                    <Trash2 className="w-4 h-4 inline mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-md w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-800">
                {editingContent ? "Edit Content" : "Create Content"}
              </h2>
              <button onClick={resetForm} className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., When mom catches you online 😂"
                />
              </div>

              {/* Type selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                <div className="flex gap-3">
                  {(["GIF", "Video", "Image"] as const).map((t) => (
                    <label key={t} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value={t}
                        checked={type === t}
                        onChange={() => {
                          setType(t);
                          setContentFile(null);
                          setContentFilePreview("");
                          setImageFile(null);
                          setImageFilePreview("");
                          setVideoFile(null);
                          setVideoFilePreview("");
                          setGifSlotIsVideo(false);
                        }}
                        className="w-4 h-4 accent-indigo-600"
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* ── GIF slot (accepts GIF + video) ─────────────── */}
              {type === "GIF" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Upload GIF or Video *{" "}
                    <span className="text-xs text-slate-400">
                      (GIF ≤ 10MB · MP4/MOV/WEBM ≤ 50MB, auto-converts to GIF)
                    </span>
                  </label>

                  {/* Show upload area only when nothing is selected yet */}
                  {!contentFile && (
                    <label className="cursor-pointer block">
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 font-medium">
                          Click to upload GIF or Video
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          .gif, .mp4, .mov, .avi, .webm accepted
                        </p>
                      </div>
                      {/* ── accept both GIF and video mimetypes ── */}
                      <input
                        type="file"
                        accept="image/gif,video/mp4,video/quicktime,video/x-msvideo,video/webm,video/mpeg,.gif,.mp4,.mov,.avi,.webm,.mkv"
                        onChange={handleGifFileUpload}
                        className="hidden"
                      />
                    </label>
                  )}

                  {/* Preview + clear button once a file is selected */}
                  {contentFile && (
                    <div className="flex items-center gap-4 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      {/* Thumbnail: image for GIF, video tag for video */}
                      {gifSlotIsVideo ? (
                        <video
                          src={contentFilePreview}
                          className="w-20 h-20 rounded-lg object-cover border bg-black"
                          muted
                        />
                      ) : (
                        <img
                          src={contentFilePreview}
                          className="w-20 h-20 rounded-lg object-cover border"
                          alt="GIF preview"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">
                          {contentFile.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {(contentFile.size / (1024 * 1024)).toFixed(2)} MB
                          {gifSlotIsVideo && (
                            <span className="ml-2 text-amber-600 font-medium">
                              → will be converted to GIF
                            </span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setContentFile(null);
                          setContentFilePreview("");
                          setGifSlotIsVideo(false);
                        }}
                        className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {/* Show existing file info when editing */}
                  {editingContent &&
                    editingContent.type === "GIF" &&
                    editingContent.contentType === "file" &&
                    !contentFile && (
                      <p className="text-xs text-slate-500 mt-2">
                        Current GIF: {editingContent.contentUrl?.split("/").pop()}
                        <span className="ml-1 text-slate-400">(upload a new file to replace)</span>
                      </p>
                    )}
                </div>
              )}

              {/* ── Video slot (file upload only) ──────────────── */}
              {type === "Video" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Upload Video *{" "}
                    <span className="text-xs text-slate-400">
                      (MP4/MOV/WEBM ≤ 50MB, 5-60 seconds)
                    </span>
                  </label>

                  {/* Video file upload area */}
                  {!videoFile && (
                    <label className="cursor-pointer block">
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 font-medium">
                          Click to upload Video
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          MP4, MOV, WEBM, AVI (max 50MB, 5-60 seconds)
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-msvideo,video/mpeg,.mp4,.mov,.webm,.avi,.mkv"
                        onChange={handleVideoFileUpload}
                        className="hidden"
                      />
                    </label>
                  )}

                  {/* Video file preview + clear */}
                  {videoFile && (
                    <div className="flex items-center gap-4 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <video
                        src={videoFilePreview}
                        className="w-20 h-20 rounded-lg object-cover border bg-black"
                        muted
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">
                          {videoFile.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                          <span className="ml-2 text-amber-600 font-medium">
                            ✓ Duration: 5-60 seconds
                          </span>
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setVideoFile(null);
                          setVideoFilePreview("");
                        }}
                        className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {/* Show existing file info when editing */}
                  {editingContent &&
                    editingContent.type === "Video" &&
                    editingContent.contentType === "file" &&
                    !videoFile && (
                      <p className="text-xs text-slate-500 mt-2">
                        Current video: {editingContent.contentUrl?.split("/").pop()}
                        <span className="ml-1 text-slate-400">(upload a new file to replace)</span>
                      </p>
                    )}
                </div>
              )}

              {/* ── Image slot ──────────────────────────────────── */}
              {type === "Image" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Upload Image *{" "}
                    <span className="text-xs text-slate-400">(PNG / JPG / WEBP ≤ 10MB)</span>
                  </label>

                  {/* Show upload area only when nothing is selected yet */}
                  {!imageFile && (
                    <label className="cursor-pointer block">
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 font-medium">
                          Click to upload Image
                        </p>
                        <p className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP accepted</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileUpload}
                        className="hidden"
                      />
                    </label>
                  )}

                  {/* Preview + clear once selected */}
                  {imageFile && (
                    <div className="flex items-center gap-4 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <img
                        src={imageFilePreview}
                        className="w-20 h-20 rounded-lg object-cover border"
                        alt="Image preview"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">
                          {imageFile.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {(imageFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setImageFile(null);
                          setImageFilePreview("");
                        }}
                        className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {/* Show existing file info when editing */}
                  {editingContent &&
                    editingContent.type === "Image" &&
                    editingContent.contentType === "file" &&
                    !imageFile && (
                      <p className="text-xs text-slate-500 mt-2">
                        Current image: {editingContent.contentUrl?.split("/").pop()}
                        <span className="ml-1 text-slate-400">(upload a new file to replace)</span>
                      </p>
                    )}
                </div>
              )}

              {/* Premium toggle */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPremium}
                    onChange={(e) => setIsPremium(e.target.checked)}
                    className="w-4 h-4 accent-purple-600"
                  />
                  <span className="text-sm font-medium text-slate-700">Premium Content</span>
                </label>
              </div>

              {/* Uploading indicator */}
              {isUploading && (
                <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <Loader className="w-5 h-5 text-indigo-600 animate-spin flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-indigo-700">
                      {gifSlotIsVideo ? "Converting video to GIF…" : "Uploading…"}
                    </p>
                    <p className="text-xs text-indigo-500">
                      {gifSlotIsVideo
                        ? "This may take 10–30 seconds for larger videos"
                        : "Please wait"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={resetForm}
                disabled={isUploading}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || isUploading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUploading && <Loader className="w-4 h-4 animate-spin" />}
                {isUploading
                  ? gifSlotIsVideo
                    ? "Converting…"
                    : "Uploading…"
                  : loading
                  ? "Saving…"
                  : editingContent
                  ? "Update"
                  : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurpriseContent;